import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PopupModal } from "react-calendly";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
	ArrowRight,
	CheckCircle,
	Ruler, // Icon for measuring/custom fit
	Palette, // Icon for style/samples
	PackageOpen, // Icon for delivery
	Star,
	ShieldCheck, // Icon for warranty/guarantee
	Users, // Icon for support
	MapPin, // Reiterate Canadian Made
	Truck, // Free Shipping
	MessageSquare, // For testimonials
} from "lucide-react";

import Hero from "../components/Hero"; // Keep importing the Hero
import PopupOffer from "../components/PopupOffer"; // Keep if still needed

// Reworked HomeScreen Component
const HomeScreen = () => {
	const [openCalendly, setOpenCalendly] = useState(false);
	// State for PopupOffer (keep if you still use it for specific promotions)
	const [showPopupOffer, setShowPopupOffer] = useState(false);
	const [forcePopupOffer, setForcePopupOffer] = useState(false);

	// Product Categories - Make this static and visual
	const productCategories = [
		{
			title: "Cellular Shades",
			url: "/images/newHome/shades/cellular.jpg", // Use clear category images
			to: "/shop/info/simply-cellular",
			description: "Energy efficient & stylish insulation.",
		},
		{
			title: "Roller Shades",
			url: "/images/newHome/shades/privacy_roller.jpg",
			to: "/shop/info/roller",
			description: "Sleek, modern light control.",
		},
		{
			title: "Zebra Shades",
			url: "/images/newHome/shades/privacy_zebra.jpg",
			to: "/shop/info/zebra",
			description: "Dual layers for versatile privacy.",
		},
		{
			title: "Sheer Shades",
			url: "/images/shadeinfo/shades/sheer/sheer.jpg",
			to: "/shop/info/sheer",
			description: "Softly filter light with elegance.",
		},
	];

	// Example Testimonials (Replace with real ones!)
	const testimonials = [
		{
			quote:
				"Absolutely love my new shades! The quality is top-notch and they fit perfectly. Highly recommend!",
			author: "Sarah M., Toronto ON",
			rating: 5,
		},
		{
			quote:
				"The free samples made choosing so easy. Installation was a breeze. Great value and service.",
			author: "David L., Vancouver BC",
			rating: 5,
		},
		{
			quote:
				"Fast shipping and excellent customer support. My living room looks completely transformed.",
			author: "Emily P., Calgary AB",
			rating: 5,
		},
	];

	// Animation variants for sections
	const sectionVariants = {
		hidden: { opacity: 0, y: 40 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.6, ease: "easeOut" },
		},
	};

	return (
		<div className="overflow-x-hidden bg-white">
			<Helmet>
				{/* SEO Optimized Title & Description */}
				<title>
					Custom Window Shades Canada | Up to 50% Off | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Shop custom window blinds & shades online. Save up to 50% on Cellular, Roller, Zebra & Sheer Shades. Handcrafted in Toronto. Free Samples & Shipping!"
				/>
				{/* Add other relevant meta tags */}
			</Helmet>

			{/* Optional Popup Offer */}
			<PopupOffer
				show={showPopupOffer}
				setShow={setShowPopupOffer}
				force={forcePopupOffer}
				setForce={setForcePopupOffer}
			/>

			{/* 1. Hero Section */}
			<Hero />

			{/* 2. Product Categories Section - VISUAL & ACTION-ORIENTED */}
			<motion.section
				className="py-16 sm:py-20 bg-gray-50"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Explore Our Custom Shades
					</h2>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
						{productCategories.map((category) => (
							<motion.div
								key={category.title}
								className="group relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
								whileHover={{ y: -5 }}
							>
								<Link to={category.to} className="block">
									<img
										src={category.url}
										alt={category.title}
										className="w-full h-48 sm:h-64 object-cover transition-transform duration-300 group-hover:scale-105"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
									<div className="absolute bottom-0 left-0 p-4">
										<h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
											{category.title}
										</h3>
										<p className="text-sm text-white/90 hidden sm:block">
											{category.description}
										</p>
										<span className="mt-2 inline-flex items-center text-white p-2 text-sm font-medium group-hover:underline bg-primary rounded-xl">
											Shop Now <ArrowRight className="ml-1 w-4 h-4" />
										</span>
									</div>
								</Link>
							</motion.div>
						))}
					</div>
					<div className="text-center mt-12">
						<Link
							to="/shop"
							className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-md hover:bg-primary/90 transition-all duration-300"
						>
							View All Products
						</Link>
					</div>
				</div>
			</motion.section>

			{/* 3. How It Works Section - SIMPLIFY the process */}
			<motion.section
				className="py-16 sm:py-20"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Your Perfect Shades in 3 Simple Steps
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-center">
						{[
							{
								icon: <Palette className="w-10 h-10 mx-auto text-primary" />,
								title: "1. Explore & Sample",
								description:
									"Browse styles and order up to 5 free fabric samples to find your perfect match.",
							},
							{
								icon: <Ruler className="w-10 h-10 mx-auto text-primary" />,
								title: "2. Measure & Customize",
								description:
									"Easily measure your windows (we help!) and customize your shades online.",
							},
							{
								icon: (
									<PackageOpen className="w-10 h-10 mx-auto text-primary" />
								),
								title: "3. Order & Install",
								description:
									"Place your order and receive your custom, Canadian-made shades quickly.",
							},
						].map((step, index) => (
							<motion.div
								key={step.title}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.1, duration: 0.5 }}
								viewport={{ once: true }}
								className="p-6"
							>
								<div className="mb-4">{step.icon}</div>
								<h3 className="text-xl font-semibold mb-2">{step.title}</h3>
								<p className="text-gray-600">{step.description}</p>
							</motion.div>
						))}
					</div>
					<div className="text-center mt-10">
						<Link
							to="/guides/measuring-guide" // Link to a dedicated measurement guide
							className="text-primary hover:underline font-medium"
						>
							Need help measuring? Check our guide{" "}
							<ArrowRight className="inline w-4 h-4" />
						</Link>
					</div>
				</div>
			</motion.section>

			{/* 4. Why Choose Off Cut Shades Section - Build TRUST & reinforce VALUE */}
			<motion.section
				className="py-16 sm:py-20 bg-gradient-to-b from-primary/5 to-white"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Quality & Service You Can Trust
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{[
							{
								icon: <MapPin className="w-8 h-8 text-primary" />,
								title: "Proudly Canadian Made",
								description:
									"Handcrafted with precision in our Toronto facility using quality materials.",
							},
							{
								icon: <Truck className="w-8 h-8 text-primary" />,
								title: "Fast, Free Shipping",
								description:
									"Enjoy complimentary shipping across Canada on all orders.",
							},
							{
								icon: <Users className="w-8 h-8 text-primary" />,
								title: "Expert Support",
								description:
									"Our dedicated team is here to help via chat, email, or phone.",
							},
							{
								icon: <ShieldCheck className="w-8 h-8 text-primary" />,
								title: "Satisfaction Guarantee",
								description:
									"We stand by our quality with a robust warranty and support.",
							}, // Add specifics if possible
						].map((feature) => (
							<div key={feature.title} className="flex items-start gap-4 p-4">
								<div className="mt-1 flex-shrink-0">{feature.icon}</div>
								<div>
									<h3 className="text-lg font-semibold mb-1">
										{feature.title}
									</h3>
									<p className="text-gray-600">{feature.description}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</motion.section>

			{/* 5. Customer Testimonials Section - SOCIAL PROOF */}
			<motion.section
				className="py-16 sm:py-20"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						See What Our Customers Are Saying
					</h2>
					{/* Basic Grid Layout - Consider a Slider for more testimonials */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{testimonials.map((testimonial, index) => (
							<motion.div
								key={index}
								className="bg-gray-50 p-6 rounded-lg shadow-sm"
								initial={{ opacity: 0, scale: 0.95 }}
								whileInView={{ opacity: 1, scale: 1 }}
								transition={{ delay: index * 0.1, duration: 0.4 }}
								viewport={{ once: true }}
							>
								<div className="flex items-center mb-2">
									{[...Array(testimonial.rating)].map((_, i) => (
										<Star
											key={i}
											className="w-5 h-5 text-yellow-500 fill-current"
										/>
									))}
									{[...Array(5 - testimonial.rating)].map((_, i) => (
										<Star
											key={i + testimonial.rating}
											className="w-5 h-5 text-gray-300"
										/>
									))}
								</div>
								<p className="text-gray-700 italic mb-4">
									"{testimonial.quote}"
								</p>
								<p className="font-semibold text-gray-800">
									- {testimonial.author}
								</p>
							</motion.div>
						))}
					</div>
					{/* Optional: Link to a dedicated reviews page */}
					{/* <div className="text-center mt-10">
                        <Link to="/reviews" className="text-primary hover:underline font-medium">
                            Read More Reviews <ArrowRight className="inline w-4 h-4"/>
                        </Link>
                    </div> */}
				</div>
			</motion.section>

			{/* 6. Sample Request Section - Strong CTA, simplified design */}
			<motion.section
				className="py-16 sm:py-20 bg-secondary text-white" // Use a brand color
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">
						Find Your Perfect Fabric Match
					</h2>
					<p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
						Not sure which color or material? Order up to 5 free samples
						delivered right to your door.
					</p>
					<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
						<Link
							to="/swatches"
							className="inline-flex items-center px-10 py-3 bg-white text-secondary rounded-lg font-semibold text-lg shadow-md group transition-colors hover:bg-gray-100"
						>
							Request Free Samples
							<ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
						</Link>
					</motion.div>
				</div>
			</motion.section>

			{/* 7. Consultation / Help Section - Reassure and offer help */}
			<section className="py-16 sm:py-20">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						Need Expert Advice?
					</h2>
					<p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
						Whether you need help measuring, choosing styles, or have any
						questions, our experts are ready to assist.
					</p>
					<motion.button
						onClick={() => setOpenCalendly(true)}
						className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-md hover:bg-primary/90 transition-all duration-300"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						Book a Free Consultation
					</motion.button>
					{/* Add links to Contact Us or FAQ pages */}
					<p className="mt-6 text-gray-600">
						Or visit our{" "}
						<Link to="/contact" className="text-primary hover:underline">
							Contact Page
						</Link>{" "}
						for more options.
					</p>
				</div>
			</section>

			{/* Calendly Popup Modal */}
			<PopupModal
				url="https://calendly.com/offcutshades"
				rootElement={document.getElementById("root")}
				open={openCalendly}
				onModalClose={() => setOpenCalendly(false)}
			/>
		</div>
	);
};

export default HomeScreen;
